"use client";

import { useRef } from "react";

interface OtpInputProps {
    value: string[];
    onChange: (next: string[]) => void;
    length?: number;
}

/**
 * Entrada de código de N dígitos com auto-avanço, backspace e colagem.
 *
 * Colar o código inteiro funciona em qualquer um dos campos: os dígitos são
 * distribuídos a partir dele e o foco vai para o próximo vazio (ou para o
 * último preenchido). O mesmo caminho atende o autofill de SMS do celular, que
 * entrega o código todo de uma vez num campo só.
 */
export function OtpInput({ value, onChange, length = 5 }: OtpInputProps) {
    const refs = useRef<(HTMLInputElement | null)[]>([]);

    /** Escreve `digits` a partir de `start` e devolve o índice do último escrito. */
    const fillFrom = (start: number, digits: string) => {
        const next = Array.from({ length }, (_, i) => value[i] ?? "");
        const usable = digits.slice(0, length - start);

        for (let i = 0; i < usable.length; i += 1) next[start + i] = usable[i]!;
        onChange(next);

        return start + usable.length - 1;
    };

    /** Foca o campo seguinte ao último escrito, sem passar do fim. */
    const focusAfter = (index: number) => {
        refs.current[Math.min(index + 1, length - 1)]?.focus();
    };

    return (
        <div className="flex justify-center gap-2" role="group" aria-label="Código de verificação">
            {Array.from({ length }).map((_, i) => (
                <input
                    key={i}
                    ref={(el) => {
                        refs.current[i] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    // Um campo por dígito, mas o autofill do SMS manda o código
                    // inteiro no primeiro: `onChange` trata isso como colagem.
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    maxLength={1}
                    aria-label={`Dígito ${i + 1}`}
                    value={value[i] ?? ""}
                    onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "");
                        if (!digits) {
                            // Apagou o dígito do campo (backspace/delete).
                            onChange(
                                Array.from({ length }, (_, index) =>
                                    index === i ? "" : (value[index] ?? "")
                                )
                            );
                            return;
                        }
                        focusAfter(fillFrom(i, digits));
                    }}
                    onPaste={(e) => {
                        // Sempre assume o controle: o input tem maxLength=1 e o
                        // navegador descartaria todos os dígitos menos o primeiro.
                        e.preventDefault();
                        const digits = e.clipboardData.getData("text").replace(/\D/g, "");
                        if (!digits) return;
                        focusAfter(fillFrom(i, digits));
                    }}
                    onFocus={(e) => e.target.select()}
                    onKeyDown={(e) => {
                        if (e.key === "Backspace" && !value[i] && i > 0) refs.current[i - 1]?.focus();
                        if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
                        if (e.key === "ArrowRight" && i < length - 1) refs.current[i + 1]?.focus();
                    }}
                    className="size-12 rounded-lg border border-base-300 bg-base-100 text-center text-lg font-semibold text-base-content focus:border-primary/55 focus:outline-none"
                />
            ))}
        </div>
    );
}
